import React, { useState, useEffect } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { motion } from "framer-motion";
import ProjectCard from "./ProjectCard";

let cachedProjects = null;
let projectsRequest = null;
const cachedLanguagesByRepo = new Map();
const languageRequestsByRepo = new Map();

const itemVariants = {
	hidden: { opacity: 0, y: 24, scale: 0.98 },
	show: (index) => ({
		opacity: 1,
		y: 0,
		scale: 1,
		transition: {
			duration: 0.45,
			delay: Math.min(0.08 * index, 0.5),
			ease: [0.22, 1, 0.36, 1],
		},
	}),
};

const ProjectCardGrid = () => {
	const [projects, setProjects] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [hasLoadError, setHasLoadError] = useState(false);
	const skeletonCards = [0, 1, 2, 3];

	useEffect(() => {
		let isMounted = true;

		const repoKey = (owner, repoName) => `${owner}/${repoName}`.toLowerCase();

		const toProjectCardModel = (repo) => ({
			id: repo.id,
			owner: repo.owner || "",
			repoName: repo.name || "",
			label: repo.name,
			description: repo.description || "No description provided.",
			details: "",
			gitlink: repo.private ? "" : repo.html_url,
			play: repo.homepage || "",
			images: [],
			languages: [],
			languagesLoading: true,
		});

		const fetchProjects = async () => {
			const response = await fetch("/.netlify/functions/github-repo");
			if (!response.ok) {
				throw new Error("Unable to fetch repositories");
			}

			const repos = await response.json();
			if (!Array.isArray(repos)) {
				return [];
			}

			return repos.map(toProjectCardModel);
		};

		const fetchProjectLanguages = async (owner, repoName) => {
			const cacheKey = repoKey(owner, repoName);
			if (cachedLanguagesByRepo.has(cacheKey)) {
				return cachedLanguagesByRepo.get(cacheKey);
			}

			if (!languageRequestsByRepo.has(cacheKey)) {
				const request = fetch(
					`/.netlify/functions/github-repo-languages?owner=${encodeURIComponent(
						owner
					)}&repo=${encodeURIComponent(repoName)}`
				)
					.then(async (response) => {
						if (!response.ok) {
							return [];
						}

						const languagesData = await response.json();
						const languages = Array.isArray(languagesData.languages)
							? languagesData.languages
							: [];
						cachedLanguagesByRepo.set(cacheKey, languages);
						return languages;
					})
					.catch(() => [])
					.finally(() => {
						languageRequestsByRepo.delete(cacheKey);
					});

				languageRequestsByRepo.set(cacheKey, request);
			}

			return languageRequestsByRepo.get(cacheKey);
		};

		const hydrateLanguages = (baseProjects) => {
			baseProjects.forEach(async (project) => {
				const languages = await fetchProjectLanguages(
					project.owner,
					project.repoName
				);

				if (!isMounted) {
					return;
				}

				setProjects((currentProjects) =>
					currentProjects.map((currentProject) =>
						currentProject.id === project.id
							? {
									...currentProject,
									languages,
									languagesLoading: false,
							  }
							: currentProject
					)
				);
			});
		};

		const loadProjects = async () => {
			setHasLoadError(false);

			if (cachedProjects && cachedProjects.length > 0) {
				if (isMounted) {
					setProjects(cachedProjects);
					setIsLoading(false);
					hydrateLanguages(cachedProjects);
				}
				return;
			}

			if (!projectsRequest) {
				projectsRequest = fetchProjects();
			}

			try {
				const fetchedProjects = await projectsRequest;
				if (Array.isArray(fetchedProjects)) {
					cachedProjects = fetchedProjects;
				}
			} catch (error) {
				console.error("Error fetching projects:", error);
				if (isMounted) {
					setProjects([]);
					setHasLoadError(true);
				}
			} finally {
				projectsRequest = null;
			}

			if (isMounted && cachedProjects) {
				setProjects(cachedProjects);
				hydrateLanguages(cachedProjects);
			}

			if (isMounted) {
				setIsLoading(false);
			}
		};

		loadProjects();

		return () => {
			isMounted = false;
		};

	}, []);

	if (isLoading) {
		return (
			<Row className="m-md-1">
				{skeletonCards.map((item) => (
					<Col md={12} key={`project-skeleton-${item}`}>
						<Card
							className={
								item % 2 === 0
									? "mt-3 p-2 pt-3 bg-dark text-light"
									: "mt-3 p-2 pt-3 bg-light text-dark"
							}
						>
							<Card.Body className="d-flex align-items-center justify-content-between">
								<div className="d-flex align-items-center w-75">
									<div className="project-skeleton project-skeleton-title"></div>
								</div>
								<div className="project-skeleton project-skeleton-button d-none d-md-block"></div>
							</Card.Body>
							<Card.Footer
								className={
									item % 2 === 0
										? "mt-3 bg-dark footer-close"
										: "mt-3 bg-light footer-close"
								}
							>
								<div className="language-marquee-viewport">
									<div className="language-marquee-track">
										<span className="language-pill-skeleton"></span>
										<span className="language-pill-skeleton"></span>
										<span className="language-pill-skeleton"></span>
										<span className="language-pill-skeleton"></span>
									</div>
								</div>
							</Card.Footer>
						</Card>
					</Col>
				))}
			</Row>
		);
	}

	if (hasLoadError) {
		return (
			<Row className="m-md-1 text-danger">
				Unable to load GitHub projects. Run with Netlify functions enabled.
			</Row>
		);
	}

	return (
		<Row className="m-md-1" as={motion.div}>
			{projects && projects.map((project, i) => (
				<Col
					md={12}
					key={project.id || i}
					as={motion.div}
					variants={itemVariants}
					initial="hidden"
					animate="show"
					custom={i}
				>
					<ProjectCard
						label={project.label}
						description={project.description}
						shade={i % 2 === 0 ? true : false}
						languages={project.languages}
						languagesLoading={project.languagesLoading}
						details={project.details}
						link={project.gitlink}
						play={project.play}
						images={project.images}
					/>
				</Col>
			))}
		</Row>
	);
};

export default ProjectCardGrid;
